import { useEffect } from 'react';
import { StringUtils } from '@manifoldxyz/js-ts-utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  disableCloseButton?: boolean;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
}

const modalSizes: Record<NonNullable<ModalProps['size']>, string> = {
  sm: StringUtils.classnames('md:m-claim--max-w-[400px]'),
  md: StringUtils.classnames('md:m-claim--max-w-[500px]'),
  lg: StringUtils.classnames('md:m-claim--max-w-[800px]'),
  full: StringUtils.classnames('md:m-claim--max-w-[90vw]'),
};

export default function Modal({
  isOpen,
  onClose,
  disableCloseButton = false,
  children,
  size = 'md', // default size
}: ModalProps) {
  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - full screen with blur effect */}
      <div
        className={StringUtils.classnames(
          'm-claim--fixed',
          'm-claim--inset-0',
          'm-claim--z-50',
          'm-claim--bg-black/50', // Semi-transparent black background
          'm-claim--backdrop-blur-sm', // Add blur effect
        )}
        onClick={onClose}
        role="presentation"
      />

      {/* Modal container */}
      <div
        className={StringUtils.classnames(
          'm-claim--fixed',
          'm-claim--inset-0',
          'm-claim--z-50',
          'm-claim--flex',
          'm-claim--items-center', // Center vertically
          'm-claim--justify-center', // Center horizontally
        )}
      >
        {/* Modal content */}
        <div
          className={StringUtils.classnames(
            'm-claim--bg-surface-secondary',
            'm-claim--w-full',
            'm-claim--h-full',
            'm-claim--flex',
            'm-claim--flex-col',
            'm-claim--overflow-hidden',
            'm-claim--max-h-full',
            // Mobile: full screen with no rounded corners
            // Desktop: maintain original styling
            'md:m-claim--h-auto',
            'md:m-claim--rounded-lg',
            'md:m-claim--max-h-[90vh]',
            modalSizes[size], // Apply size variant - now prefixed with md: to only apply on desktop
          )}
        >
          {/* Close button - Only show if not disabled */}
          {!disableCloseButton && (
            <button
              onClick={onClose}
              className={StringUtils.classnames(
                'm-claim--absolute',
                'm-claim--right-4',
                'm-claim--top-4',
                'm-claim--p-2',
                'm-claim--text-secondary',
                'hover:m-claim--text-primary',
                'm-claim--transition-colors',
                'm-claim--z-[60]', // Added higher z-index
                'm-claim--bg-surface-accent', // Added background color
                'm-claim--rounded-full', // Added full rounded border radius
              )}
              aria-label="Close modal"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="m-claim--size-6"
              >
                <path
                  d="M6 18L18 6M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* Modal content - Scrollable container with proper padding for mobile & desktop */}
          <div className="m-claim--flex-1 m-claim--overflow-y-auto">
            <div className="m-claim--h-full m-claim--p-4 sm:m-claim--h-auto md:m-claim--p-6">
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
